import { AxiosInstance } from "axios";
import { useQuery, type UseQueryOptions } from "react-query";
import * as z from "zod";
import { useApi } from "@/hooks/useApi";

const Repository = z.object({
  name: z.coerce.string(),
});

export type Repository = z.infer<typeof Repository>;

const SearchResult = z.object({
  data: z.array(Repository),
  has_more: z.boolean(),
});

export type SearchResult = z.infer<typeof SearchResult>;

/**
 * Low-level helper: hit a `{data, has_more}` endpoint and validate the
 * response. The endpoint is the caller's choice.
 *
 * Repository endpoints share the same pagination / search contract on the
 * backend (`UserRepositoryService::paginatedSearch`), so the only thing
 * that varies between callers is the URL + auth namespace.
 */
export async function searchRepositories({
  api,
  endpoint,
  search,
  perPage = 100,
  extraParams,
}: {
  api: AxiosInstance;
  endpoint: string;
  search: string;
  perPage?: number;
  /** Extra query params to send with the repository search. */
  extraParams?: Record<string, string | number | undefined>;
}): Promise<SearchResult> {
  const response = await api.get(endpoint, {
    params: { search, per_page: perPage, ...extraParams },
  });
  return SearchResult.parse(response.data);
}

/**
 * React Query wrapper around `searchRepositories` shared by the
 * migration wizard and the env builder wizard. Both features have the
 * same data-layer needs:
 *
 * - debounced server-side search (parent supplies the debounced value)
 * - `keepPreviousData` so the dropdown doesn't flicker between keystrokes
 * - 30s `staleTime` (responses change slowly)
 *
 * UI shells differ (multi-select vs single-select, per-row content,
 * footer affordances) so each feature still owns its own dropdown.
 */
export function useRepositorySearch({
  endpoint,
  search,
  perPage = 100,
  enabled = true,
  queryKey,
  extraParams,
  options,
}: {
  endpoint: string;
  search: string;
  perPage?: number;
  enabled?: boolean;
  /** React Query cache key — feature-scoped so caches don't collide. */
  queryKey: readonly unknown[];
  extraParams?: Record<string, string | number | undefined>;
  options?: Omit<
    UseQueryOptions<SearchResult, unknown, SearchResult, readonly unknown[]>,
    "queryKey" | "queryFn"
  >;
}) {
  const api = useApi();

  return useQuery(
    queryKey,
    () => searchRepositories({ api, endpoint, search, perPage, extraParams }),
    {
      enabled,
      keepPreviousData: true,
      staleTime: 30_000,
      ...options,
    },
  );
}
